import { LitElement, html } from 'lit';
import * as portfolioApi from './portfolio-api';
import portfolioEvents from './portfolio-events';

import dispatchCustomEvent from '../../utils/dispatch-custom-event';
import translate from '../../utils/webcomponents/translate';
import { isValidTrackingObject } from '../../tracking/utils';

const succesTemplate = () => {
	const labelProductRemoved = translate('portfolio', 'productRemoved', 'Product removed from portfolio');
	const labelClose = translate('portfolio', 'close', 'Close');

	return html`
		<x-message
			modifier="positive"
			icon="checkmark"
			message="${labelProductRemoved}"
		></x-message>

		<div class="form__footer form__footer--centered">
			<button
				type="button"
				class="button js-close"
			>
				${labelClose}
			</button>
		</div>
	`;
};

const bodyTemplate = (gtmPushData, shouldTrack) => {
	const labelProductRemoveAreYouSure = translate('portfolio', 'productRemoveAreYouSure', '');
	const labeLCancel = translate('portfolio', 'cancel', 'Cancel');
	const labeLConfirm = translate('portfolio', 'confirm', 'Confirm');

	return html`
		<div class="trailer text-center">${labelProductRemoveAreYouSure}</div>

		<div class="form__footer form__footer--centered">
			<button
				class="button js-yes"
				?data-gtm-click="${shouldTrack}"
				data-gtm-push="${gtmPushData}"
			>
				${labeLConfirm}
			</button>
			<button class="button button--ghost js-close">${labeLCancel}</button>
		</div>
	`;
};

class PortfolioItemRemoveForm extends LitElement {
	static get properties() {
		return {
			isError: { type: Boolean },
			isSuccess: { type: Boolean },
			isLoading: { type: Boolean },

			options: {
				type: Object,
			},
		};
	}

	constructor() {
		super();

		this.isError = false;
		this.isSuccess = false;
		this.isLoading = false;

		this.onClick = this.onClick.bind(this);
	}

	async firstUpdated() {
		super.firstUpdated();

		this.addEventListener('click', this.onClick);
	}

	disconnectedCallback() {
		this.removeEventListener('click', this.onClick);
		super.disconnectedCallback();
	}

	updated() {
		dispatchCustomEvent(this, 'updated');
	}

	render() {
		const gtmPushData = JSON.stringify(this.options.gtmPushData);
		const shouldTrack = isValidTrackingObject(this.options.gtmPushData);

		return html`
			<x-loader .isLoading="${this.isLoading}"></x-loader>

			${this.isError
				? html`
						<x-message
							modifier="error"
							icon="warning"
							message="${translate('portfolio', 'somethingWentWrong', 'Something went wrong')}"
						></x-message>
					`
				: ''}
			${this.isSuccess ? succesTemplate() : bodyTemplate(gtmPushData, shouldTrack)}
		`;
	}

	onClick(e) {
		const isConfirmButton = e.target.classList && e.target.classList.contains('js-yes');

		if (isConfirmButton) {
			this.removeItem();
		}
	}

	onSuccess() {
		this.isSuccess = true;

		// dispatch it from body, otherwise IE11 can't catch it in portfolio
		dispatchCustomEvent(document.body, portfolioEvents.ITEM_REMOVE_SUCCESS, this.options);
	}

	close() {
		dispatchCustomEvent(this, 'finish');
	}

	async removeItem() {
		const { portfolioId, id } = this.options;

		this.isLoading = true;
		this.isError = false;

		try {
			await portfolioApi.remove(portfolioId, id);

			this.onSuccess();
		} catch (e) {
			this.isError = true;
		} finally {
			this.isLoading = false;
		}
	}

	dispatchCustomEvent(eventName, detail = {}, dispatcher = this) {
		const event = new CustomEvent(eventName, {
			bubbles: true,
			detail,
		});

		dispatcher.dispatchEvent(event);
	}

	createRenderRoot() {
		return this;
	}
}

customElements.define('x-portfolio-item-remove-form', PortfolioItemRemoveForm);

export default PortfolioItemRemoveForm;
